import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CalendarPlus,
  CheckCircle,
  ClipboardText,
  Copy,
  Eye,
  Lightning,
  PaperPlaneTilt,
  PlayCircle,
  Plus,
  RocketLaunch,
  WarningCircle,
} from "@phosphor-icons/react";

const STEPS = [
  {
    id: "create-workflow",
    label: "1. Create a workflow",
    icon: Plus,
    title: "Create a recurring workflow",
    text: "Open Workflow Center and click Create workflow. Choose the tool, name the workflow, set the day and time, then keep Require approval enabled so drafts are reviewed before anything becomes an action task.",
    tip: "Use clear names like Weekly Local AI posts, Monthly SEO content, or Review reply workflow so the Action Board is easy to understand later.",
  },
  {
    id: "run-or-wait",
    label: "2. Run or wait",
    icon: PlayCircle,
    title: "Run it now or let automation handle it",
    text: "Use Run now when testing. For live automation, leave the workflow active and Ordeneli will pick it up on the background runner. The runner checks scheduled work roughly every 10 minutes.",
    tip: "Only run one workflow at a time when testing. If another workflow is busy, wait for it to finish before starting the next one.",
  },
  {
    id: "review-approval",
    label: "3. Review the approval",
    icon: Eye,
    title: "Check the draft before approving",
    text: "Generated drafts appear in Approvals. Click View to read the output, copy it, or check whether it is useful enough to move into the Action Board.",
    tip: "Reject anything that is not ready. Approvals are there so content never moves forward without a review step.",
  },
  {
    id: "approve-draft",
    label: "4. Approve the draft",
    icon: CheckCircle,
    title: "Approve the draft",
    text: "When the draft looks good, click Approve. Ordeneli creates a ready action task from that approved draft and moves it into the Action Board.",
    tip: "Approved tasks stay available after refresh because they are saved in the backend action task table.",
  },
  {
    id: "generate-content",
    label: "5. Generate posts or content",
    icon: RocketLaunch,
    title: "Generate publish-ready content from the plan",
    text: "From a plan task, generate LinkedIn posts, GBP posts, WordPress posts, WordPress landing pages, or blog posts. These outputs are designed to become real customer-facing content, not just internal planning notes.",
    tip: "GBP can generate multiple short post options. WordPress posts, landing pages, and blog posts should read like finished content with real paragraphs and clear calls to action.",
  },
  {
    id: "save-action-task",
    label: "6. Save as action task",
    icon: ClipboardText,
    title: "Save the generated output as a new action task",
    text: "After generating content, click Save as action task. This stores the generated content properly so it remains after refresh and can use the normal Schedule, Publish, Complete, and Archive buttons.",
    tip: "Do this before scheduling. Preview-only content is temporary, but saved action tasks are stored and can be managed safely.",
  },
  {
    id: "schedule-or-publish",
    label: "7. Schedule or publish",
    icon: CalendarPlus,
    title: "Schedule, publish, complete, or archive",
    text: "For social tasks, use Schedule one post or LinkedIn preview. For WordPress tasks, use WordPress preview to create a draft in your connected WordPress site. You can also mark tasks as published, complete them, or archive old tasks.",
    tip: "Scheduled posts are checked by the backend runner. A task scheduled for 10:00 may publish on the next runner check, usually around 10:10–10:15 depending on server timing.",
  },
];

const CONTENT_TYPES = [
  ["Generate GBP posts from this plan", "Creates multiple short Google Business Profile style posts that can be selected one at a time."],
  ["Generate LinkedIn post from this plan", "Creates finished LinkedIn-ready copy based on the plan without exposing the internal plan details."],
  ["Generate WordPress post from this plan", "Creates a WordPress-ready article or post draft with a proper title, body copy, and CTA."],
  ["Generate WordPress landing page from this plan", "Creates landing page copy with headline, sections, benefits, FAQs, and a conversion-focused CTA."],
  ["Generate blog post from this plan", "Creates a full blog article with paragraphs, useful explanation, and a clear next step."],
];

function StepCard({ step }) {
  const Icon = step.icon;

  return (
    <section id={step.id} className="border border-[#E5E7EB] bg-white p-5 sm:p-6 scroll-mt-24">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 bg-[#002FA7] text-white flex items-center justify-center shrink-0">
          <Icon size={22} weight="duotone" />
        </div>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#002FA7]">
            {step.label}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">
            {step.title}
          </h2>
          <p className="mt-3 text-[#525252] leading-relaxed">
            {step.text}
          </p>
          <div className="mt-4 border border-[#BFDBFE] bg-[#EFF6FF] p-3 text-sm text-[#1E40AF]">
            <strong>Tip:</strong> {step.tip}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function WorkflowDocs() {
  return (
    <div data-testid="workflow-docs-page">
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
            Workflow Center Guide
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter">
            How to use Workflow Center
          </h1>
          <p className="mt-4 text-[#525252] text-base sm:text-lg max-w-2xl">
            Turn AI plans into approvals, saved action tasks, scheduled posts, LinkedIn content, WordPress drafts, and publish-ready marketing work.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/workflow" className="btn-primary" data-testid="workflow-docs-open-workflow">
              Open Workflow Center <ArrowUpRight size={16} weight="bold" />
            </Link>
            <Link to="/pricing" className="btn-secondary">
              View Pro plan
            </Link>
          </div>
        </div>
      </section>

      <section className="container-wide py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <nav className="lg:sticky lg:top-24 border-l border-[#E5E7EB]" data-testid="workflow-docs-toc">
            {STEPS.map((step) => (
              <a
                key={step.id}
                href={`#${step.id}`}
                className="block pl-4 py-2 -ml-px border-l-2 border-transparent text-sm text-[#525252] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
              >
                {step.label}
              </a>
            ))}
            <a
              href="#content-types"
              className="block pl-4 py-2 -ml-px border-l-2 border-transparent text-sm text-[#525252] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
            >
              Content outputs
            </a>
            <a
              href="#troubleshooting"
              className="block pl-4 py-2 -ml-px border-l-2 border-transparent text-sm text-[#525252] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
            >
              Troubleshooting
            </a>
          </nav>
        </aside>

        <main className="lg:col-span-9 order-1 lg:order-2 space-y-6">
          <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <Lightning size={22} weight="duotone" className="text-[#002FA7] shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display text-2xl font-black tracking-tight">The simple flow</h2>
                <p className="mt-2 text-[#525252]">
                  Create workflow → run or wait → review approval → approve draft → generate content → save as action task → schedule or publish.
                </p>
              </div>
            </div>
          </div>

          {STEPS.map((step) => (
            <StepCard key={step.id} step={step} />
          ))}

          <section id="content-types" className="border border-[#E5E7EB] bg-white p-5 sm:p-6 scroll-mt-24">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
                <Copy size={22} weight="duotone" />
              </div>
              <div className="w-full">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">
                  Content outputs
                </p>
                <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">
                  What each generator should produce
                </h2>
                <div className="mt-5 border border-[#E5E7EB] overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F5F5F5]">
                      <tr>
                        <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Button</th>
                        <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Expected output</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {CONTENT_TYPES.map(([name, description]) => (
                        <tr key={name}>
                          <td className="px-4 py-3 font-semibold whitespace-nowrap">{name}</td>
                          <td className="px-4 py-3 text-[#525252]">{description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          <section id="troubleshooting" className="border border-[#FECACA] bg-[#FEF2F2] p-5 sm:p-6 scroll-mt-24">
            <div className="flex items-start gap-4">
              <WarningCircle size={24} weight="duotone" className="text-[#DC2626] shrink-0 mt-1" />
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#B91C1C]">
                  Troubleshooting
                </p>
                <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">
                  Quick checks
                </h2>
                <ul className="mt-4 space-y-2 text-[#525252] list-disc pl-5">
                  <li>If generated content disappears after refresh, make sure you clicked <strong>Save as action task</strong>.</li>
                  <li>If Schedule says the task cannot be found, the generated output was not saved as a backend action task yet.</li>
                  <li>If LinkedIn publishing is unavailable, connect a LinkedIn account first.</li>
                  <li>If WordPress publishing is unavailable, connect a WordPress site with an Application Password first.</li>
                  <li>If a scheduled post does not publish instantly, wait for the next backend runner check.</li>
                </ul>
              </div>
            </div>
          </section>
        </main>
      </section>
    </div>
  );
}
